import React from 'react'
import { Box, IconButton, Typography, useTheme } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useFormContext, useWatch } from 'react-hook-form'
import MultiSelectApiAutoComplete from '../../../components/FormElements/MultiSelectApiAutoComplete'
import MultiSelectWithCheckBox, { RenderTagsType } from '../../../components/FormElements/MultiSelectWithCheckBox'
import FormSelect from '../FormSelect'
import { grey, red } from '../../../colors'
import {
  type CardError,
  type PermissionCardForm,
  type QuestionTypeOption,
  ReportAccess,
  type ReportPermissionRole,
  ReportRoleType
} from '../types'
import { QUESTION_TYPE_OPTIONS, cardShowsTaResults, makeEmptyCard } from '../utils'

const ROLES_ENDPOINT = '/api/report_permissions/roles/'

const ROLE_TYPE_OPTIONS = [
  { label: 'Academic Unit', value: ReportRoleType.ACADEMIC_UNIT },
  { label: 'Course Section', value: ReportRoleType.COURSE_SECTION }
]

const REPORT_ACCESS_OPTIONS = [
  {
    label: 'Full Access',
    value: ReportAccess.FULL_ACCESS,
    description: 'View reports and export results, including individual (raw) responses.'
  },
  {
    label: 'View Results Only',
    value: ReportAccess.VIEW_RESULTS_ONLY,
    description: 'View reports and export aggregate results — but not individual (raw) responses.'
  },
  {
    label: 'No Access',
    value: ReportAccess.NO_ACCESS,
    description: 'Cannot view reports or results.'
  }
]

const TA_RESULTS_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false }
]

// The shared multi-select reserves a fixed min-height for its (unused here) helper slot;
// collapse it so the field hugs its content and lines up with the Role Type dropdown.
const COMPACT_MULTISELECT = { '& > .MuiBox-root': { minHeight: 'unset' } }

const FieldLabel: React.FC<{ children: React.ReactNode, required?: boolean }> = ({ children, required }) => (
  <Typography sx={{ fontWeight: 600, fontSize: '14px', color: grey[250], mb: 1 }}>
    {children}{required === true && ' *'}
  </Typography>
)

const HelperText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: '13px', color: grey[850], mt: 0.5 }}>{children}</Typography>
)

const ErrorText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: '13px', color: red[700], mt: 0.5 }}>{children}</Typography>
)

const reportAccessHelper = (roleType: ReportRoleType | ''): string =>
  roleType === ReportRoleType.COURSE_SECTION
    ? 'Defines whether this role can view and export the results of this Survey Project for their Course Sections.'
    : 'Defines whether this role can view and export the results of this Survey Project for their managed Academic Unit.'

const QUESTION_TYPES_HELPER =
  "Question types not selected will be hidden entirely from this role's reports. All types are visible unless you deselect them."

interface PermissionGroupCardProps {
  index: number
  canEdit: boolean
  onRemove: (index: number) => void
  /** Role IDs already used by sibling cards — computed by the parent (which already
   *  watches the full `cards` array for validation) so this card doesn't need its own
   *  subscription to every sibling's edits. */
  taken: Set<string>
  cardError?: CardError
  /** Reveal validation messages — true only after a Save attempt. */
  showErrors?: boolean
}

const PermissionGroupCard: React.FC<PermissionGroupCardProps> = ({ index, canEdit, onRemove, taken, cardError, showErrors = false }) => {
  const theme = useTheme()
  const { setValue, control } = useFormContext()
  const base = `cards.${index}` as const

  // Scoped to this card only — watching the full `cards` array here would re-subscribe
  // every card to every sibling's edits, which is what caused every card to re-render on
  // any keystroke in any other card.
  const card = (useWatch({ control, name: base }) ?? makeEmptyCard()) as PermissionCardForm
  const roleType = card.roleType ?? ''

  const showR2 = roleType !== '' && card.reportAccess !== ReportAccess.NO_ACCESS
  const showR3 = cardShowsTaResults(card)

  const handleRoleTypeChange = (): void => {
    // Switching Role Type clears the Roles selection (no mixed-type groups).
    setValue(`${base}.roles`, [])
  }

  return (
    <Box
      sx={{
        backgroundColor: grey[100],
        borderRadius: theme.spacing(2),
        padding: theme.spacing(4),
        marginBottom: theme.spacing(3)
      }}
    >
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -1 }}>
          <IconButton aria-label="Remove permission group" onClick={() => { onRemove(index) }} sx={{ padding: 0, color: red[700] }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Role Type + Roles share a row, per the mockup. */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        <Box sx={{ flex: '0 0 38%' }}>
          <FieldLabel required>Role Type</FieldLabel>
          <FormSelect
            name={`${base}.roleType`}
            label="Role Type"
            showLabel={false}
            disabled={!canEdit}
            options={ROLE_TYPE_OPTIONS}
            onChange={handleRoleTypeChange}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>Roles</FieldLabel>
          <Box sx={COMPACT_MULTISELECT}>
            <MultiSelectApiAutoComplete<ReportPermissionRole>
              name={`${base}.roles`}
              apiEndpoint={ROLES_ENDPOINT}
              apiExtraParams={{ roleType }}
              staleTime={10 * 60 * 1000}
              skip={roleType === ''}
              disabled={!canEdit || roleType === ''}
              readonly={!canEdit}
              // Select All ignores getOptionDisabled, so it would pick already-used roles
              // and break one-group-per-role. Disable it for this field.
              showSelectAll={false}
              renderTagsType={RenderTagsType.Chips}
              placeholder="Select roles"
              textFieldStyles={{ backgroundColor: grey[0], flexWrap: 'wrap' }}
              getOptionLabel={(r) => r.roleName}
              getOptionValue={(r) => r.roleId}
              getOptionDisabled={(r) => taken.has(r.roleId)}
            />
          </Box>
          {showErrors && cardError?.roles != null && <ErrorText>{cardError.roles}</ErrorText>}
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <FieldLabel required>Report Access</FieldLabel>
        <FormSelect
          name={`${base}.reportAccess`}
          label="Report Access"
          showLabel={false}
          disabled={!canEdit}
          options={REPORT_ACCESS_OPTIONS}
        />
        <HelperText>{reportAccessHelper(roleType)}</HelperText>
      </Box>

      {showR2 && (
        <Box sx={{ mt: 2 }}>
          <FieldLabel>Visible Question Types</FieldLabel>
          <Box sx={COMPACT_MULTISELECT}>
            <MultiSelectWithCheckBox<QuestionTypeOption>
              name={`${base}.questionTypes`}
              options={QUESTION_TYPE_OPTIONS}
              disabled={!canEdit}
              readonly={!canEdit}
              renderTagsType={RenderTagsType.Chips}
              placeholder="Select question types"
              textFieldStyles={{ backgroundColor: grey[0], flexWrap: 'wrap' }}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.id}
            />
          </Box>
          {showErrors && cardError?.questionTypes != null
            ? <ErrorText>{cardError.questionTypes}</ErrorText>
            : <HelperText>{QUESTION_TYPES_HELPER}</HelperText>}
        </Box>
      )}

      {showR3 && (
        <Box sx={{ mt: 2 }}>
          <FieldLabel>View Teaching Assistant Results</FieldLabel>
          <FormSelect
            name={`${base}.canViewTaResults`}
            label="View Teaching Assistant Results"
            showLabel={false}
            disabled={!canEdit}
            options={TA_RESULTS_OPTIONS}
          />
          <HelperText>Enables Instructors to see the Repeat per TA sections on their course sections.</HelperText>
        </Box>
      )}
    </Box>
  )
}

// The parent recomputes `taken` and `cardError` fresh on every render (they're plain
// function calls, not memoized), so a default shallow-prop comparison would never see them
// as equal even when their contents didn't change. Compare by value instead, so a keystroke
// in a sibling card only re-renders this one when it actually changes something relevant.
const arePropsEqual = (prev: PermissionGroupCardProps, next: PermissionGroupCardProps): boolean => {
  if (
    prev.index !== next.index ||
    prev.canEdit !== next.canEdit ||
    prev.showErrors !== next.showErrors ||
    prev.onRemove !== next.onRemove
  ) {
    return false
  }
  if (prev.taken.size !== next.taken.size || Array.from(prev.taken).some((id) => !next.taken.has(id))) {
    return false
  }
  return prev.cardError?.roles === next.cardError?.roles && prev.cardError?.questionTypes === next.cardError?.questionTypes
}

export default React.memo(PermissionGroupCard, arePropsEqual)
